/**
 * PocketFiler - Notifications Preferences
 * Settings tab — control which emails you receive.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n';
import api from '../lib/api';
import { toast } from 'sonner';
import { Bell, Mail, Shield, Loader2, Save } from 'lucide-react';


const PREFERENCES = [
  {
    group: 'account',
    title: 'Account & Security',
    items: [
      { key: 'security_alerts', label: 'Security alerts (failed logins, new sessions, 2FA changes)', always: true, description: 'Required for account safety.' },
      { key: 'email_verification_reminders', label: 'Email verification reminders', default: true },
      { key: 'password_expiry', label: 'Password expiry reminders', default: true },
    ],
  },
  {
    group: 'activity',
    title: 'Activity & Deals',
    items: [
      { key: 'deal_status', label: 'Deal status changes', default: true },
      { key: 'document_shared', label: 'Someone shares a document with you', default: true },
      { key: 'signature_complete', label: 'Signature requests & completions', default: true },
      { key: 'meeting_reminders', label: 'Meeting reminders (15 min before start)', default: true },
      { key: 'task_reminders', label: 'Task due-date reminders', default: true },
    ],
  },
  {
    group: 'product',
    title: 'Product & Marketing',
    items: [
      { key: 'onboarding_tips', label: 'Onboarding tips (first 30 days)', default: true },
      { key: 'weekly_digest', label: 'Weekly activity digest', default: false },
      { key: 'product_updates', label: 'Product updates & changelogs', default: true },
      { key: 'newsletter', label: 'Newsletter & tips', default: false },
    ],
  },
];


export default function NotificationsTab() {
  const { t } = useTranslation();
  const [prefs, setPrefs] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const data = await api.get('/notifications/preferences');
      setPrefs(data.preferences || {});
    } catch (e) {
      // Set defaults if endpoint fails or returns nothing
      const defaults = {};
      PREFERENCES.forEach(g => {
        g.items.forEach(item => {
          defaults[item.key] = item.default !== undefined ? item.default : true;
        });
      });
      setPrefs(defaults);
    }
    setLoading(false);
  };

  const toggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/notifications/preferences', { preferences: prefs });
      toast.success(t('notifications.saved') || 'Preferences saved');
      setDirty(false);
    } catch (e) {
      toast.error(t('notifications.error') || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border bg-muted/30 p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-1">{t('notifications.title') || 'Notification Preferences'}</h3>
          <p className="text-xs text-muted-foreground">
            {t('notifications.subtitle') || 'Control which emails you receive. Security alerts can\'t be turned off.'}
          </p>
        </div>
      </div>

      {PREFERENCES.map(group => (
        <div key={group.group} className="rounded-xl border bg-background">
          <div className="px-4 py-3 border-b">
            <h4 className="font-semibold text-sm flex items-center gap-2">
              {group.group === 'account' && <Shield className="h-4 w-4 text-primary" />}
              {group.group === 'activity' && <Mail className="h-4 w-4 text-primary" />}
              {group.group === 'product' && <Bell className="h-4 w-4 text-primary" />}
              {t(`notifications.group.${group.group}`) || group.title}
            </h4>
          </div>
          <div className="divide-y">
            {group.items.map(item => {
              const checked = item.always ? true : !!prefs[item.key];
              return (
                <div key={item.key} className="px-4 py-3 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{t(`notifications.${item.key}.label`) || item.label}</p>
                    {item.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => !item.always && toggle(item.key)}
                    disabled={item.always}
                    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${
                      checked ? 'bg-primary' : 'bg-muted-foreground/20'
                    } ${item.always ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    role="switch"
                    aria-checked={checked}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform mt-0.5 ${
                        checked ? 'translate-x-5' : 'translate-x-0.5'
                      } shadow`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Save button */}
      <div className="sticky bottom-0 bg-background py-3 border-t">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? (t('notifications.saving') || 'Saving...') : (t('notifications.save') || 'Save Preferences')}
        </button>
      </div>
    </div>
  );
}
